<template>
  <div class="wechat-profile-page">
    <div class="base_content">
      <div class="page-head">
        <div>
          <div class="page-title">收款商户档案</div>
          <div class="base_tips mt5">当前新模板只支持平台小程序支付，小程序支付无需配置 JSAPI 支付授权目录。</div>
        </div>
        <div>
          <el-button size="small" icon="el-icon-back" @click="$router.back()">返回</el-button>
          <el-button v-if="canSyncStatus" size="small" type="primary" :loading="syncingStatus" @click="syncStatus">同步状态</el-button>
          <el-button v-if="canShowConfirmCode" size="small" @click="openConfirm">查看确认二维码</el-button>
          <el-button v-if="canShowConfirmCode" size="small" :disabled="!canSyncConfirmStatus" :loading="confirmSyncing" @click="syncConfirm">同步确认状态</el-button>
          <el-button v-if="canCheckReady" size="small" type="primary" :loading="checkingReady" @click="checkReady">支付就绪检查</el-button>
          <el-button v-if="canEnableProfile" size="small" type="warning" :loading="enablingProfile" @click="enable">标记为可绑定</el-button>
          <el-button v-if="canOpenBinding" size="small" type="success" @click="goBinding">查看收款绑定</el-button>
        </div>
      </div>
    </div>

    <div class="base_content mt10">
      <div v-if="loading" class="profile-loading-tip">资料加载中...</div>
      <el-alert
        v-if="nonCurrentProfileNotice"
        class="mb10"
        type="warning"
        :closable="false"
        :title="nonCurrentProfileNotice"
      />
      <wechat-applyment-status-panel
        :applyment="applyment"
        :profile="profile"
        :block-reason="enableBlockReason"
      />
      <div class="section-title">基础信息</div>
      <div class="info-grid">
        <div class="info-item"><span>门店</span>{{ storeName }}</div>
        <div class="info-item"><span>特约商户号</span>{{ profile.sub_mchid || applyment.sub_mchid || "-" }}</div>
        <div class="info-item"><span>小程序 AppID</span>{{ miniProgramAppid || "-" }}</div>
        <div class="info-item"><span>申请单号</span>{{ applyment.applyment_id || "-" }}</div>
        <div class="info-item"><span>申请状态</span>{{ applymentStatusText(definedValue(applyment.status, profile.applyment_status)) }}</div>
        <div class="info-item"><span>签约状态</span>{{ signStatusText(profile.sign_status) }}</div>
        <div class="info-item"><span>商户确认状态</span>{{ accountConfirmText(definedValue(applyment.account_confirm_status, profile.account_confirm_status)) }}</div>
        <div class="info-item"><span>小程序 AppID 状态</span>{{ appidStatusText(profile.appid_config_status) }}</div>
        <div class="info-item"><span>JSAPI授权目录状态</span>{{ jsapiStatusText(profile.jsapi_path_status) }}</div>
        <div class="info-item"><span>JSAPI授权目录</span>{{ profile.jsapi_path || "无需配置" }}</div>
        <div class="info-item"><span>分账关系状态</span>{{ profitSharingText(profile.profit_sharing_status) }}</div>
        <div class="info-item"><span>最大分账比例</span>{{ ratioText(profile.max_profit_sharing_ratio) }}</div>
        <div class="info-item">
          <span>主商城分账比例</span>
          <el-tag size="mini" style="width: auto;" :type="platformProfitRuleTag">{{ platformProfitRuleText }}</el-tag>
        </div>
        <div class="info-item">
          <span>档案状态</span>
          <el-tag size="mini" style="width: auto;" :type="profileEnableStatus.type">
            {{ profileEnableStatus.text }}
          </el-tag>
        </div>
        <div class="info-item"><span>绑定商户</span>{{ currentProfile.sub_mchid || "-" }}</div>
        <div class="info-item"><span>当前申请单号</span>{{ currentProfile.applyment_id || "-" }}</div>
        <div class="info-item"><span>最后同步时间</span>{{ lastSyncAt || "-" }}</div>
      </div>

      <div class="capability-section">
        <div class="section-title">商家转账到零钱</div>
        <div class="capability-head">
          <el-tag size="mini" :type="transferAuthorizationStatusTag">
            {{ transferAuthorizationStatusText }}
          </el-tag>
          <div class="capability-actions">
            <el-button
              v-if="canApplyTransferAuthorization"
              size="small"
              type="primary"
              :loading="applyingTransferAuthorization"
              @click="applyTransfer"
            >
              申请授权
            </el-button>
            <el-button
              v-if="canRefreshTransferAuthorization"
              size="small"
              :loading="refreshingTransferAuthorization"
              @click="refreshTransfer"
            >
              刷新状态
            </el-button>
          </div>
        </div>
        <div class="info-grid">
          <div class="info-item"><span>特约商户号</span>{{ transferAuthorizationSubMchid || "-" }}</div>
          <div class="info-item"><span>申请单号</span>{{ transferAuthorizationApplymentId || "-" }}</div>
          <div class="info-item"><span>最后同步时间</span>{{ transferAuthorizationLastSyncAt || "-" }}</div>
          <div class="info-item"><span>错误信息</span>{{ transferAuthorizationError || "-" }}</div>
        </div>
      </div>

      <el-alert
        v-if="applyment.reject_reason"
        class="mt15"
        type="error"
        :closable="false"
        :title="applyment.reject_reason"
      />

      <div v-if="canShowProfitRuleAction" class="profile-actions">
        <el-button size="small" type="primary" @click="goProfitRule">{{ profitRuleButtonText }}</el-button>
      </div>
    </div>

    <el-dialog title="商户确认二维码" :visible.sync="confirmDialog.show" width="420px">
      <div v-if="confirmDialog.url" class="confirm-box">
        <img v-if="confirmDialog.qr" :src="confirmDialog.qr" class="confirm-qr" alt="确认二维码" />
        <el-input v-model="confirmDialog.url" readonly size="small">
          <el-button slot="append" @click="copyConfirmUrl">复制</el-button>
        </el-input>
        <div class="base_tips mt10">请商户负责人使用微信扫码完成确认。确认完成后点击同步确认状态。</div>
      </div>
      <el-alert v-else type="warning" :closable="false" title="微信暂未返回确认链接，请先同步状态或查看驳回原因。" />
    </el-dialog>
  </div>
</template>

<script>
import QR from "@/components/common/wxqrcode.js";
import { eltips } from "@/util/util.js";
import WechatApplymentStatusPanel from "./components/WechatApplymentStatusPanel.vue";
import {
  applyTransferAuthorization,
  checkProfileReadiness,
  enableProfile,
  refreshTransferAuthorization,
  syncApplyment,
  syncApplymentConfirm,
} from "@/api/weishop/wechatApplyment";
import {
  applymentStatusValue,
  canCheckApplymentReadiness,
  canShowApplymentConfirmCode,
  canSyncApplymentConfirm,
  canSyncApplymentStatus,
  confirmQrForUrl,
  confirmUrlForApplyment,
} from "./utils/wechatApplymentForm";
import {
  isRegionScope,
  resolveWeiShopApi,
} from "@/views/apps/weishop/region/utils/regionApiMap";

const {
  shouldShowMerchantProfileEnableFallback,
} = require("./utils/wechatMerchantProfile");

export default {
  components: {
    WechatApplymentStatusPanel,
  },
  data() {
    return {
      loading: false,
      storeMap: { 0: "主商城" },
      applyment: {},
      profile: {},
      currentProfile: {},
      profitSharingRule: {},
      transferAuthorization: {},
      formData: {},
      syncingStatus: false,
      confirmSyncing: false,
      checkingReady: false,
      enablingProfile: false,
      applyingTransferAuthorization: false,
      refreshingTransferAuthorization: false,
      detailRequestSeq: 0,
      confirmDialog: {
        show: false,
        url: "",
        qr: "",
      },
    };
  },
  computed: {
    storeName() {
      const id = this.currentStoreId;
      return this.storeMap[id] || (id === 0 ? "主商城" : `门店 #${id}`);
    },
    currentStoreId() {
      return Number(this.definedValue(
        this.applyment.store_id,
        this.definedValue(this.profile.store_id, this.definedValue(this.$route.query.store_id, 0))
      ));
    },
    isHeadProfile() {
      return this.currentStoreId === 0;
    },
    isRegionAccount() {
      return isRegionScope();
    },
    canShowProfitRuleAction() {
      return !this.isRegionAccount;
    },
    currentApplymentStatus() {
      return applymentStatusValue(this.applyment, this.profile);
    },
    miniProgramAppid() {
      const business = this.formData.business || {};
      return (
        this.profile.sub_appid ||
        business.mini_program_sub_appid ||
        business.sub_appid ||
        this.formData.sub_appid ||
        ""
      );
    },
    lastSyncAt() {
      return this.profile.last_sync_at || this.applyment.updated_at || "";
    },
    confirmUrl() {
      return confirmUrlForApplyment(this.applyment, this.profile);
    },
    canSyncStatus() {
      return canSyncApplymentStatus(this.currentApplymentStatus) && !!this.applyment.id;
    },
    canShowConfirmCode() {
      return canShowApplymentConfirmCode(this.currentApplymentStatus, this.confirmUrl);
    },
    canSyncConfirmStatus() {
      return canSyncApplymentConfirm(this.currentApplymentStatus, this.confirmUrl) && !!this.applyment.id;
    },
    canCheckReady() {
      return canCheckApplymentReadiness(this.currentApplymentStatus) && !!this.profile.id;
    },
    profileReady() {
      const explicitReady = this.definedValue(this.profile.is_payment_ready, "");
      if (explicitReady !== "") {
        return Number(explicitReady) === 1;
      }

      return Number(this.profile.is_enabled) === 1 &&
        String(this.profile.sub_mchid || "") !== "" &&
        Number(this.profile.applyment_status) === 50 &&
        Number(this.profile.sign_status) === 50 &&
        [0, 20].includes(Number(this.profile.account_confirm_status)) &&
        [0, 20].includes(Number(this.profile.jsapi_path_status)) &&
        Number(this.profile.profit_sharing_status) === 50;
    },
    profileReadinessReady() {
      const explicitStatus = this.definedValue(
        this.profile.payment_ready_status,
        this.definedValue(this.profile.readiness_status, "")
      );
      if (explicitStatus !== "") {
        return [1, 50].includes(Number(explicitStatus));
      }

      return String(this.profile.sub_mchid || "") !== "" &&
        Number(this.profile.applyment_status) === 50 &&
        Number(this.profile.sign_status) === 50 &&
        [0, 20].includes(Number(this.profile.account_confirm_status)) &&
        [0, 20].includes(Number(this.profile.jsapi_path_status)) &&
        Number(this.profile.profit_sharing_status) === 50 &&
        (
          !String(this.profile.sub_appid || "") ||
          Number(this.profile.appid_config_status) === 20
        );
    },
    profileEnableStatus() {
      if (Number(this.profile.is_enabled) === 1) {
        return { text: "可绑定", type: "success" };
      }

      if (this.profileReadinessReady) {
        return { text: "待标记", type: "warning" };
      }

      return { text: "待检查", type: "info" };
    },
    currentProfileMatchesApplyment() {
      const applymentId = String(this.applyment.applyment_id || "");
      const currentApplymentId = String(this.currentProfile.applyment_id || "");
      if (applymentId && currentApplymentId) {
        return applymentId === currentApplymentId;
      }

      const subMchid = String(this.applyment.sub_mchid || "");
      const currentSubMchid = String(this.currentProfile.sub_mchid || "");
      return !!(subMchid && currentSubMchid && subMchid === currentSubMchid);
    },
    nonCurrentProfileNotice() {
      if (!this.currentProfile.id || this.currentProfileMatchesApplyment) {
        return "";
      }

      return `该门店已绑定商户为 ${this.currentProfile.sub_mchid || "-"}，当前查看的申请不是已绑定商户。`;
    },
    canEnableProfile() {
      return shouldShowMerchantProfileEnableFallback(
        this.profile,
        this.profileReadinessReady,
        this.profileReady
      );
    },
    canOpenBinding() {
      return Number(this.currentApplymentStatus) === 50;
    },
    bindingPath() {
      return this.isRegionAccount ? "/region/store/wechat/binding" : "/weishop/merchants/setting/wechat/store_merchant_binding";
    },
    enableBlockReason() {
      if (!this.profile.id) {
        return "请先同步生成收款商户档案。";
      }

      if (Number(this.profile.is_enabled) === 1) {
        return "";
      }

      const confirmStatus = Number(this.definedValue(this.applyment.account_confirm_status, this.profile.account_confirm_status));
      if (confirmStatus === 10 || confirmStatus === 40) {
        return "请先完成商户负责人微信确认。";
      }

      if (Number(this.profile.profit_sharing_status) !== 50) {
        return "请先完成微信分账接收方同步。";
      }

      return "支付就绪检查通过后系统会自动标记为可绑定；门店当前收款请到收款绑定页面设置。";
    },
    platformProfitRatio() {
      return Number(this.profitSharingRule.platform_ratio || 0);
    },
    platformProfitRuleText() {
      if (this.isHeadProfile) {
        return "不适用";
      }

      if (this.platformProfitRatio > 0) {
        return this.ratioText(this.platformProfitRatio);
      }

      return "未配置";
    },
    platformProfitRuleTag() {
      if (this.isHeadProfile) {
        return "info";
      }

      return this.platformProfitRatio > 0 ? "success" : "warning";
    },
    profitRuleButtonText() {
      return this.isHeadProfile ? "管理门店分账规则" : "设置主商城分账";
    },
    transferAuthorizationStatusValue() {
      return Number(this.definedValue(
        this.transferAuthorization.status,
        this.definedValue(this.transferAuthorization.authorization_status, this.transferAuthorization.auth_status)
      ));
    },
    transferAuthorizationStatusText() {
      if (this.transferAuthorization.status_text) {
        return this.transferAuthorization.status_text;
      }

      const map = {
        10: "未申请",
        20: "未授权",
        30: "待确认",
        40: "已授权",
        50: "已拒绝",
        60: "已过期",
        70: "已取消",
        80: "查询失败",
      };
      return map[this.transferAuthorizationStatusValue] || "未知";
    },
    transferAuthorizationStatusTag() {
      if (this.transferAuthorizationStatusValue === 40) {
        return "success";
      }

      if (this.transferAuthorizationStatusValue === 30) {
        return "warning";
      }

      if ([20, 50].indexOf(this.transferAuthorizationStatusValue) !== -1) {
        return "danger";
      }

      return "info";
    },
    transferAuthorizationSubMchid() {
      return this.transferAuthorization.sub_mchid || this.profile.sub_mchid || this.applyment.sub_mchid || "";
    },
    transferAuthorizationApplymentId() {
      return (
        this.transferAuthorization.out_apply_no ||
        this.transferAuthorization.applyment_id ||
        this.transferAuthorization.authorization_applyment_id ||
        this.transferAuthorization.out_batch_no ||
        ""
      );
    },
    transferAuthorizationLastSyncAt() {
      return this.transferAuthorization.last_synced_at || this.transferAuthorization.last_sync_at || this.transferAuthorization.synced_at || this.transferAuthorization.updated_at || "";
    },
    transferAuthorizationError() {
      return (
        this.transferAuthorization.reject_reason ||
        this.transferAuthorization.fail_reason ||
        this.transferAuthorization.error_message ||
        this.transferAuthorization.last_error ||
        ""
      );
    },
    canRefreshTransferAuthorization() {
      return !!this.applyment.id && !!this.transferAuthorizationSubMchid;
    },
    canApplyTransferAuthorization() {
      return (
        this.canRefreshTransferAuthorization &&
        this.transferAuthorizationStatusValue !== 40 &&
        this.transferAuthorizationStatusValue !== 30
      );
    },
  },
  created() {
    this.loadStores();
    this.loadDetail();
  },
  watch: {
    "$route.fullPath"() {
      this.resetDetailState();
      this.loadStores();
      this.loadDetail();
    },
  },
  methods: {
    isOk(res) {
      return res && (res.code === 200 || res.status === 200);
    },
    msg(res, fallback) {
      return (res && (res.msg || res.message)) || fallback;
    },
    definedValue(value, fallback) {
      return value === undefined || value === null ? fallback : value;
    },
    resetDetailState() {
      this.applyment = {};
      this.profile = {};
      this.currentProfile = {};
      this.profitSharingRule = {};
      this.transferAuthorization = {};
      this.formData = {};
    },
    loadStores() {
      Promise.resolve()
        .then(() => this.axios.get(resolveWeiShopApi("/api/weishop/admin/plugin/store/information/index"), { params: { page: 1, limit: 500 } }))
        .then((res) => {
          if (!this.isOk(res)) return;
          const list = (((res.data || {}).data || {}).data || (res.data || {}).data || []);
          this.storeMap = { 0: "主商城" };
          (Array.isArray(list) ? list : []).forEach((item) => {
            this.storeMap[item.id] = item.title;
          });
        })
        .catch((error) => {
          console.error("wechat merchant profile stores load failed", error);
        });
    },
    requestApplymentDetail(id) {
      // 详情页和门店列表共用组件内 axios，请求会经过同一套登录头、店铺头和区域账号映射拦截器。
      return this.axios.get(resolveWeiShopApi(`/api/weishop/admin/wechat-applyments/${id}`), {
        params: { _t: Date.now() },
        timeout: 15000,
      });
    },
    async loadDetail() {
      const id = this.$route.query.id;
      if (!id) {
        eltips("缺少商户资料ID", "error");
        this.loading = false;
        return;
      }
      const requestSeq = this.detailRequestSeq + 1;
      this.detailRequestSeq = requestSeq;
      this.loading = true;
      const closeLoading = () => {
        if (requestSeq !== this.detailRequestSeq) return;
        this.loading = false;
        this.$nextTick(() => {
          this.loading = false;
        });
      };
      const loadingTimer = window.setTimeout(() => {
        if (requestSeq !== this.detailRequestSeq) return;
        console.warn("wechat merchant profile detail request timeout", { id });
        closeLoading();
      }, 15000);
      try {
        const res = await this.requestApplymentDetail(id);
        // 同一路由组件会被复用，只有最后一次详情请求可以写入页面状态。
        if (requestSeq !== this.detailRequestSeq) return;
        const detail = this.normalizeDetailData(this.extractDetailPayload(res));
        // 详情接口历史上存在 response.data 和直接 data 两种包装；只要有进件或档案主体，就先回填页面，避免 200 响应被包装差异误判为空。
        if (this.isOk(res) || this.hasDetailPayload(detail)) {
          this.applyDetailData(detail);
        } else {
          eltips(this.msg(res, "获取商户档案失败"), "error");
        }
      } catch (error) {
        if (requestSeq !== this.detailRequestSeq) return;
        console.error("wechat merchant profile load failed", error);
        try {
          eltips("获取商户档案失败，请刷新后重试", "error");
        } catch (tipsError) {
          console.error("wechat merchant profile error tips failed", tipsError);
        }
      } finally {
        window.clearTimeout(loadingTimer);
        // 只允许当前请求关闭当前 loading，旧请求完成时不能影响新页面。
        closeLoading();
      }
    },
    extractDetailPayload(res) {
      const raw = res && typeof res === "object" ? res : {};
      let payload = raw.data && typeof raw.data === "object" ? raw.data : raw;

      if (payload.data && typeof payload.data === "object" && (payload.data.applyment || payload.data.profile)) {
        payload = payload.data;
      }

      return payload;
    },
    normalizeDetailData(data) {
      const payload = data && typeof data === "object" ? data : {};
      const applyment = payload.applyment && typeof payload.applyment === "object"
        ? payload.applyment
        : (payload.id || payload.applyment_id ? payload : {});

      return {
        applyment,
        profile: payload.profile && typeof payload.profile === "object" ? payload.profile : {},
        currentProfile: payload.current_profile && typeof payload.current_profile === "object" ? payload.current_profile : {},
        profitSharingRule: payload.profit_sharing_rule && typeof payload.profit_sharing_rule === "object" ? payload.profit_sharing_rule : {},
        transferAuthorization: payload.transfer_authorization && typeof payload.transfer_authorization === "object" ? payload.transfer_authorization : {},
        formData: payload.form_data && typeof payload.form_data === "object" ? payload.form_data : (applyment.form_data || {}),
      };
    },
    hasDetailPayload(detail) {
      const data = detail && typeof detail === "object" ? detail : {};
      // 驳回、撤回等申请可能还没有 profile，只要 applyment 存在就应该能打开档案页展示状态和驳回原因。
      return !!(
        (data.applyment && (data.applyment.id || data.applyment.applyment_id)) ||
        (data.profile && (data.profile.id || data.profile.sub_mchid))
      );
    },
    applyDetailData(detail) {
      const data = detail && typeof detail === "object" ? detail : {};
      // 统一入口写入详情状态，后续新增展示区时避免在 loadDetail 中继续堆赋值逻辑。
      this.applyment = data.applyment || {};
      this.profile = data.profile || {};
      this.currentProfile = data.currentProfile || {};
      this.profitSharingRule = data.profitSharingRule || {};
      this.transferAuthorization = data.transferAuthorization || {};
      this.formData = data.formData || {};
    },
    applyTransfer() {
      if (!this.canApplyTransferAuthorization) return;
      this.applyingTransferAuthorization = true;
      applyTransferAuthorization(this.applyment.id)
        .then((res) => {
          if (this.isOk(res)) {
            this.updateTransferAuthorizationFromResponse(res);
            eltips(this.msg(res, "申请授权成功"), "success");
            this.loadDetail();
          } else {
            eltips(this.msg(res, "申请授权失败"), "error");
          }
        })
        .finally(() => {
          this.applyingTransferAuthorization = false;
        });
    },
    refreshTransfer() {
      if (!this.canRefreshTransferAuthorization) return;
      this.refreshingTransferAuthorization = true;
      refreshTransferAuthorization(this.applyment.id)
        .then((res) => {
          if (this.isOk(res)) {
            this.updateTransferAuthorizationFromResponse(res);
            eltips(this.msg(res, "刷新状态成功"), "success");
            this.loadDetail();
          } else {
            eltips(this.msg(res, "刷新状态失败"), "error");
          }
        })
        .finally(() => {
          this.refreshingTransferAuthorization = false;
        });
    },
    updateTransferAuthorizationFromResponse(res) {
      if (res && res.data && res.data.transfer_authorization) {
        this.transferAuthorization = res.data.transfer_authorization;
      }
    },
    syncStatus() {
      if (!this.canSyncStatus) return;
      this.syncingStatus = true;
      syncApplyment(this.applyment.id)
        .then((res) => {
          if (this.isOk(res)) {
            eltips(this.msg(res, "同步成功"), "success");
            this.loadDetail();
          } else {
            eltips(this.msg(res, "同步失败"), "error");
          }
        })
        .finally(() => {
          this.syncingStatus = false;
        });
    },
    syncConfirm() {
      if (!this.canSyncConfirmStatus) return;
      this.confirmSyncing = true;
      syncApplymentConfirm(this.applyment.id)
        .then((res) => {
          if (this.isOk(res)) {
            eltips(this.msg(res, "同步成功"), "success");
            this.loadDetail();
          } else {
            eltips(this.msg(res, "同步失败"), "error");
          }
        })
        .finally(() => {
          this.confirmSyncing = false;
        });
    },
    checkReady() {
      if (!this.canCheckReady) return;
      this.checkingReady = true;
      checkProfileReadiness(this.profile.id)
        .then((res) => {
          if (this.isOk(res)) {
            eltips(this.msg(res, "检查成功"), "success");
            this.loadDetail();
          } else {
            eltips(this.msg(res, "检查失败"), "error");
          }
        })
        .finally(() => {
          this.checkingReady = false;
        });
    },
    enable() {
      if (!this.canEnableProfile) return;
      this.enablingProfile = true;
      enableProfile(this.profile.id)
        .then((res) => {
          if (this.isOk(res)) {
            eltips(this.msg(res, "档案已标记为可绑定"), "success");
            this.loadDetail();
          } else {
            eltips(this.msg(res, "标记可绑定失败"), "error");
          }
        })
        .finally(() => {
          this.enablingProfile = false;
        });
    },
    goBinding() {
      this.$router.push({
        path: this.bindingPath,
        query: this.currentStoreId ? { store_id: this.currentStoreId } : {},
      });
    },
    openConfirm() {
      const url = this.confirmUrl;
      this.confirmDialog = {
        show: true,
        url,
        qr: confirmQrForUrl(url, (value) => QR.createQrCodeImg(value, { size: 220 })),
      };
    },
    copyConfirmUrl() {
      if (this.confirmDialog.url && navigator.clipboard) {
        navigator.clipboard.writeText(this.confirmDialog.url);
      }
      eltips("确认链接已复制", "success");
    },
    goProfitRule() {
      const storeId = this.currentStoreId;
      this.$router.push({
        path: "/weishop/merchants/finance/wechat_profit_rule",
        query: storeId > 0 ? { store_id: storeId } : {},
      });
    },
    applymentStatusText(status) {
      const map = { 10: "草稿", 20: "已提交", 30: "审核中", 40: "待确认/签约", 50: "已完成", 60: "已驳回", 70: "已撤回" };
      return map[Number(status)] || "未知";
    },
    signStatusText(status) {
      return Number(status) === 50 ? "已签约" : "未完成";
    },
    accountConfirmText(status) {
      const map = { 0: "无需确认", 10: "待确认", 20: "已确认" };
      return map[Number(status)] || "未确认";
    },
    appidStatusText(status) {
      return Number(status) === 20 ? "已配置" : "未完成";
    },
    jsapiStatusText(status) {
      const map = { 0: "无需配置", 10: "待配置", 20: "已配置" };
      return map[Number(status)] || "未完成";
    },
    profitSharingText(status) {
      return Number(status) === 50 ? "已同步" : "未同步";
    },
    ratioText(value) {
      if (value === undefined || value === null || value === "") return "-";
      return `${(Number(value) * 100).toFixed(2)}%`;
    },
  },
};
</script>

<style lang="less" scoped>
.wechat-profile-page {
  .page-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .page-title {
    color: #303133;
    font-size: 18px;
    font-weight: 600;
  }

  .section-title {
    margin-top: 16px;
    margin-bottom: 12px;
    color: #303133;
    font-size: 16px;
    font-weight: 600;
  }

  .profile-loading-tip {
    margin-bottom: 12px;
    color: #909399;
    font-size: 13px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(260px, 1fr));
    border-top: 1px solid #ebeef5;
    border-left: 1px solid #ebeef5;
  }

  .info-item {
    min-height: 42px;
    padding: 11px 12px;
    border-right: 1px solid #ebeef5;
    border-bottom: 1px solid #ebeef5;
    color: #303133;

    span {
      display: inline-block;
      width: 130px;
      color: #909399;
    }
  }

  .profile-actions {
    margin-top: 18px;
  }

  .capability-section {
    margin-top: 18px;
  }

  .capability-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .capability-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .confirm-box {
    text-align: center;
  }

  .confirm-qr {
    display: block;
    width: 220px;
    height: 220px;
    margin: 0 auto 16px;
  }
}
</style>
